
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { checkAuthToken } from '@/utils/authUtils';

interface UserInfo {
  email: string;
  firstName: string;
  lastName: string;
  accountId: string;
  contactId: string;
  accountNumber: string | null;  // Google Drive folder ID
  refreshToken: string | null;   // Google Drive refresh token
  ssoId: string | null;          // SSO provider subject claim (full sub, e.g. google-oauth2|105183...)
}

interface AuthContextType {
  isAuthenticated: boolean;
  hasAccess: boolean | null; // null = checking, true = has access, false = no access
  isCheckingAccess: boolean;
  accessError: string | null;
  user: UserInfo | null;
  login: () => void;
  logout: () => void;
  checkAccess: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);

  const checkAccess = async () => {
    const token = localStorage.getItem('bearer_token');
    if (!token) {
      setHasAccess(false);
      setAccessError('No authentication token found');
      return;
    }

    setIsCheckingAccess(true);
    setAccessError(null);

    try {
      console.log('[AuthContext] Checking user access...');

      const response = await fetch('/api/check_access', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Access check failed: ${response.status}`);
      }

      const data = await response.json();

      console.log('[AuthContext] Access check result:', data);
      console.log('[FHIR-GDRIVE] AuthContext RAW API response:', JSON.stringify(data, null, 2));

      setHasAccess(data.hasAccess);

      if (data.hasAccess && data.user) {
        setUser({
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          accountId: data.user.accountId,
          contactId: data.user.contactId,
          accountNumber: data.user.accountNumber || null,
          refreshToken: data.user.refreshToken || null,
          ssoId: data.user.ssoId || null
        });
        console.log('[AuthContext] User info stored:', {
          email: data.user.email,
          hasAccountNumber: !!data.user.accountNumber,
          hasRefreshToken: !!data.user.refreshToken
        });
        console.log('[FHIR-GDRIVE] AuthContext received from API:');
        console.log('[FHIR-GDRIVE]   - accountNumber (folder ID):', data.user.accountNumber || 'NULL/EMPTY');
        console.log('[FHIR-GDRIVE]   - refreshToken:', data.user.refreshToken ? 'SET (length: ' + data.user.refreshToken.length + ')' : 'NULL/EMPTY');
      } else {
        setUser(null);
        console.log('[FHIR-GDRIVE] AuthContext: No user data or no access');
      }

      if (!data.hasAccess) {
        setAccessError(data.message || 'You do not have access to this application');
      }
    } catch (error) {
      console.error('[AuthContext] Error checking access:', error);
      setHasAccess(false);
      setAccessError('Failed to verify access. Please try again.');
    } finally {
      setIsCheckingAccess(false);
    }
  };

  // Initialize authentication state from localStorage on mount
  useEffect(() => {
    const hasToken = checkAuthToken();
    console.log('AuthContext initializing, token exists:', hasToken);
    setIsAuthenticated(hasToken);

    // If user has token, check if they have access
    if (hasToken) {
      checkAccess();
    } else {
      setHasAccess(false);
    }
  }, []);

  const login = () => {
    console.log('AuthContext: login called');
    setIsAuthenticated(true);
    // Check access after login
    checkAccess();
  };

  const logout = () => {
    console.log('AuthContext: logout called');
    localStorage.removeItem('bearer_token');
    localStorage.removeItem('user_info');
    setIsAuthenticated(false);
    setHasAccess(false);
    setAccessError(null);
    setUser(null);
    window.location="/"; //ERS260218 goto sign in
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      hasAccess,
      isCheckingAccess,
      accessError,
      user,
      login,
      logout,
      checkAccess
    }}>
      {children}
    </AuthContext.Provider>
  );
};
